import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Plural, Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Styles
import './download-signin-modal.scss';

// Components
import GenericModal, { MODAL_TYPE } from '../generic-modal/generic-modal';
import Button, { BUTTON_STYLES } from '../button/button';
import SelectLabel from '../select-label/select-label';
import RadioButton from '../radio-button/radio-button';

// Actions
import consoleActions from '../../containers/console/actions/console-actions';

const SELECTED_STUDENTS_VALUE = 1;
const WHOLE_CLASS_VALUE = 2;
const SELECT_GROUP_VALUE = 3;

// List of VALID QR format values
const qr3x3Format = 'QR_3x3';
const qr2x2Format = 'QR';
const qr2x5Format = '_2X5';
const qr2x7Format = '_2X7';
const qr3x8Format = '_3X8';

function DownloadSignInModal(props) {
    const { i18n } = useLocalisation();

    // Props
    const {
        closeHandler,
        // State
        productId,
        currentClassId,
        classGroups,
        wholeClassStudents,
        mainSelectedStudentIds,
        // Actions
        downloadSignInCardsHandler,
        numOfSelectedStudents
    } = props;

    // Select Values
    const [qrFormatValue, setQrFormatValue] = useState(qr3x3Format);
    const [cardsSelection, setSelection] = useState(numOfSelectedStudents ? 1 : 2);
    const [groupValue, setGroupValue] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);

    // Button
    const [isDownloadDisabled, setIsDownloadDisabled] = useState(true);

    const numStudentsInClass = wholeClassStudents.length;
    // List of lists
    const listOfQRFormatOptions = [
        {
            id: '',
            name: i18n._(t`Download Sign-in - Select option label`)
        },
        {
            id: qr3x3Format,
            name: i18n._(t`Download Sign-in - QR code 3 x 3 label`)
        },
        {
            id: qr2x2Format,
            name: i18n._(t`Download Sign-in - QR code 2 x 2 label`)
        },
        {
            id: qr2x5Format,
            name: i18n._(t`Download Sign-in - 2 x 5 label`)
        },
        {
            id: qr2x7Format,
            name: i18n._(t`Download Sign-in - 2 x 7 label`)
        },
        {
            id: qr3x8Format,
            name: i18n._(t`Download Sign-in - 3 x 8 label`)
        }
    ];
    const listOfGroups = [
        {
            groupId: '',
            name: i18n._(t`Download Sign-in - Option group`)
        },
        ...classGroups
    ];
    // const groupToSelectLabel = <Trans id="Download Sign-in - Select Group Label" />;
    const listOfButtons = [
        {
            name: <Plural
                id="Download Sign-in - Option selection - {numOfSelectedStudents}"
                value={numOfSelectedStudents}
                _0="Selected Students"
                one="Selected Student (#)"
                other="Selected Students (#)"
            />,
            value: SELECTED_STUDENTS_VALUE,
            isDisabled: numOfSelectedStudents === 0
        },
        {
            name: <Trans id="Download Sign-in - Option class - {numStudentsInClass}">{numStudentsInClass}</Trans>,
            value: WHOLE_CLASS_VALUE
        },
        {
            name: <Trans id="Download Sign-in - Option group" />,
            value: SELECT_GROUP_VALUE
        }
    ];

    // Select/Radio handlers
    const handleGroupValue = (e) => {
        setGroupValue(e.target.value);
    };
    const handleQRFormatValue = (e) => {
        setQrFormatValue(e.target.value);
    };
    const radioButtonHandler = (e) => {
        const selectionValue = e.target.value;
        const value = parseInt(selectionValue, 10);
        setSelection(value);
    };

    // This function will determine what to send to the B/E
    const localDownloadSignInCardsHandler = async () => {
        const signInCardPageSizeType = qrFormatValue;
        let studentUserProfileIds = [];
        let groupId = '';

        switch (cardsSelection) {
            case SELECTED_STUDENTS_VALUE: // Selected Students
                studentUserProfileIds = mainSelectedStudentIds;
                break;
            case WHOLE_CLASS_VALUE: // Whole Class
                studentUserProfileIds = wholeClassStudents;
                break;
            case SELECT_GROUP_VALUE: // Select a group
                studentUserProfileIds = [];
                groupId = parseInt(groupValue, 10);
                break;
            default:
        }

        const response = await downloadSignInCardsHandler({
            signInCardPageSizeType,
            productId,
            groupId,
            currentClassId,
            studentUserProfileIds
        });

        if (response === false) {
            const message = <Trans id={'Error Message - Unable to proceed'} />;
            setErrorMessage(message);
        }
    };

    // Effect to handle the button being disabled
    useEffect(() => {
        const selectedStudentsValid = cardsSelection === SELECTED_STUDENTS_VALUE && mainSelectedStudentIds.length === 0;
        const groupSelectionValid = cardsSelection === SELECT_GROUP_VALUE && groupValue === '';

        const validateButton = (
            qrFormatValue === '' || cardsSelection === '' || (selectedStudentsValid) || (groupSelectionValid)
        );

        setIsDownloadDisabled(validateButton);
    }, [groupValue, qrFormatValue, cardsSelection]);

    // Effect to set the list of groups to the default group
    useEffect(() => {
        const defaultGroup = classGroups.find(element => element.isDefault === true);
        setGroupValue(defaultGroup.groupId);
    }, [classGroups]);

    return (
        <GenericModal
            title={<Trans id="Download Sign-in - title" />}
            type={MODAL_TYPE.floating}
            parentClasses="modal--small download-signin-modal"
            closeHandler={closeHandler}>
            <div className="form__row">
                <RadioButton
                    id="sign-in-selection"
                    propertyName="value"
                    groupName="sign-in"
                    value={cardsSelection}
                    listOfButtons={listOfButtons}
                    changeHandler={radioButtonHandler} />
                <SelectLabel
                    selectHandler={handleGroupValue}
                    value={groupValue}
                    ariaProps={
                        {
                            /*
                                The following value is the id of the last radio button printed just above:
                                    - 'radio-' (as any radio button),
                                    - id,
                                    - groupName,
                                    - index
                            */
                            ariaLabelledBy: 'radio-sign-in-selection-sign-in-2'
                        }
                    }
                    propertyName="groupId"
                    visibleProperty="name"
                    importedClasses="select--groups"
                    id="download-signin-select-groups"
                    isSelectDisabled={cardsSelection !== 3}
                    listOfOptions={listOfGroups}
                />
            </div>
            <div className="form__row">
                <label
                    htmlFor="download-signin-select-qr-format"
                    className="form__label">
                    <Trans
                        id={'Download Sign-in - Format label'}
                    />
                </label>
                <SelectLabel
                    selectHandler={handleQRFormatValue}
                    value={qrFormatValue}
                    propertyName="id"
                    visibleProperty="name"
                    id="download-signin-select-qr-format"
                    listOfOptions={listOfQRFormatOptions}
                />
            </div>
            <div className="form__row form__row--buttons">
                <Button
                    buttonStyle={BUTTON_STYLES.text}
                    callback={localDownloadSignInCardsHandler}
                    isBtnDisabled={isDownloadDisabled}
                    visibleText={<Trans id="Download Sign-in - button" />}
                />
            </div>
            {
                errorMessage ? (
                    <div className="form__row form__row--buttons">
                        <p className="form__error-message">{errorMessage}</p>
                    </div>
                ) : ''
            }
        </GenericModal>
    );
}

DownloadSignInModal.propTypes = {
    closeHandler: PropTypes.func,
    // State
    productId: PropTypes.number,
    currentClassId: PropTypes.number,
    classGroups: PropTypes.array,
    wholeClassStudents: PropTypes.array,
    mainSelectedStudentIds: PropTypes.array,
    // Actions
    downloadSignInCardsHandler: PropTypes.func,
    numOfSelectedStudents: PropTypes.number
};

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from MessageModalCombinedReducers.
 */
function mapStateToProps(state) {
    const {
        productId,
        currentClassId,
        classGroups,
        classGroupStudents,
        mainSelectedStudentIds
    } = state.consoleReducer;

    return {
        productId,
        currentClassId,
        classGroups,
        wholeClassStudents: classGroupStudents.map(student => student.userProfileId),
        mainSelectedStudentIds
    };
}

/*
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        downloadSignInCardsHandler: consoleActions.downloadSignInCardsHandler
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(DownloadSignInModal);



// WEBPACK FOOTER //
// ./components/download-signin-modal/download-signin-modal.js